import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import logo from "../../images/logo.png";

const Resume = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [isSigned, setIsSigned] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [originalFileName, setOriginalFileName] = useState(null);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const fetchResume = useCallback(async () => {
    if (!userId) return;

    try {
      const res = await axios.get(`${API_BASE}/api/resume/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (res.data?.resume_file) {
        setUploadedFileName(res.data.resume_file);
        setOriginalFileName(res.data.resume_original || res.data.resume_file);
      }
    } catch (err) {
      console.error("Fetch failed", err);
    }
  }, [API_BASE, userId]);

  useEffect(() => {
    fetchResume();
  }, [fetchResume]);

  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const TASK_ID = 25; // verify this matches nwt_tasks.t_id for LEEP
        const idKey = String(TASK_ID);
        const nameKey = "resume";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("Error checking signature status:", err);
      }
    };

    if (userId) fetchTaskStatus();
  }, [userId, API_BASE]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

    if (allowedTypes.includes(selected.type)) {
      setFile(selected);
    } else {
      alert("Only PDF, JPG, or PNG files are allowed!");
      setFile(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !userId) {
      alert("Select PDF file and ensure login.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);

    try {
      const res = await axios.post(
        `${API_BASE}/api/resume/upload?userId=${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setUploadedFileName(res.data.filename);
      setOriginalFileName(res.data.originalName);
      alert("Resume uploaded successfully!");
    } catch (err) {
      console.error("Upload error:", err);
      alert("Upload failed.");
    }
  };

  const handleCancel = () => {
    navigate(isAdmin && paramId ? `/userhome/${paramId}` : "/userhome");
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>Resume</span>
        <button className="btn btn-secondary" onClick={handleCancel}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img
              src={logo}
              alt="Company Logo"
              style={{ width: "150px", height: "auto" }}
            />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Resume</h2>
          </div>
          <div className="col-3 text-end">
            <strong>New-Way Trucking Ltd</strong>
            <br />
            7725 48 St. SE, Calgary
            <br />
            Alberta, Canada T2C 2V3
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label>Select File (PDF / JPG / PNG) *</label>
            <input
              type="file"
              className="form-control"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              disabled={!isAdmin && isSigned}
            />
          </div>
          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!file || (!isAdmin && isSigned)}
            >
              Upload
            </button>
          </div>
        </form>

        {uploadedFileName && (
          <div className="mt-4 p-3 bg-light border rounded">
            <div className="text-muted mb-2">Uploaded Version</div>
            <a
              href={`${API_BASE}/uploads/resumes/${uploadedFileName}`}
              target="_blank"
              rel="noopener noreferrer"
              download={originalFileName}
            >
              {originalFileName}
            </a>
          </div>
        )}

        <hr className="my-5" />

        {userId && (
          <>
            <SignatureManager
              taskName="resume"
              taskId={25}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />

            <ReviewManager
              taskName="resume"
              taskId={25}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default Resume;
